/**
 * Fetch an opaque, source-bound provider URL for a booking CTA.
 *
 * The browser supplies only its server-issued visitor bearer (and an optional
 * conversation pointer). It never sends the configured calendar URL, prospect
 * fields, or an email. Failure is deliberately `null`: callers keep using the
 * original link, so attribution can never make scheduling unavailable.
 */

export const BOOKING_ATTRIBUTION_TIMEOUT_MS = 4000;
// The API expires references after at most seven days and site retention cannot
// be configured below seven days. Refresh a day early so a long-lived SPA never
// reuses a server-expired reference.
export const BOOKING_ATTRIBUTION_CACHE_TTL_MS = 6 * 24 * 60 * 60 * 1000;

export function isBookingAttributionCacheFresh(
  expiresAtMs: number,
  nowMs: number = Date.now(),
): boolean {
  return Number.isFinite(expiresAtMs) && expiresAtMs > nowMs;
}

export interface BookingAttributionLinkOptions {
  apiUrl: string;
  clientKey: string;
  visitorToken: string;
  originalUrl: string;
  conversationId?: string | null;
  fetcher?: typeof fetch;
}

/**
 * Booking surfaces use the same resolver for both visitor clicks and automatic
 * calendar transitions. Returning void/null preserves the configured link as a
 * best-effort fallback for hosts that do not wire attribution.
 */
export type BookingIntentResolver = (
  originalUrl: string,
) => Promise<string | null | void> | string | null | void;

type BookingProvider = "cal.com" | "calendly";

const ATTRIBUTION_REFERENCE = /^[A-Za-z0-9_-]{43}$/;

function providerForHost(hostname: string): BookingProvider | null {
  const host = hostname.toLowerCase();
  if (host === "cal.com" || host.endsWith(".cal.com")) return "cal.com";
  if (host === "calendly.com" || host.endsWith(".calendly.com")) {
    return "calendly";
  }
  return null;
}

function normalizedPath(pathname: string): string {
  return pathname.replace(/\/+$/, "") || "/";
}

function supportedBookingRoute(
  value: string,
): { url: URL; provider: BookingProvider } | null {
  try {
    const url = new URL(value);
    const provider = providerForHost(url.hostname);
    if (
      url.protocol !== "https:" ||
      url.username ||
      url.password ||
      url.hash ||
      !provider
    ) {
      return null;
    }
    return { url, provider };
  } catch {
    return null;
  }
}

function hasExactAttributionQuery(url: URL, provider: BookingProvider): boolean {
  const entries = Array.from(url.searchParams.entries());
  const expectedKey =
    provider === "cal.com" ? "metadata[floe_ref]" : "utm_content";
  return (
    entries.length === 1 &&
    entries[0][0] === expectedKey &&
    ATTRIBUTION_REFERENCE.test(entries[0][1])
  );
}

/** Candidate must be the same configured provider route, not an API redirect. */
export function isMatchingAttributedBookingUrl(
  originalValue: string,
  candidateValue: unknown,
): candidateValue is string {
  if (typeof candidateValue !== "string") return false;
  const original = supportedBookingRoute(originalValue);
  const candidate = supportedBookingRoute(candidateValue);
  return !!(
    original &&
    candidate &&
    original.provider === candidate.provider &&
    original.url.origin === candidate.url.origin &&
    normalizedPath(original.url.pathname) ===
      normalizedPath(candidate.url.pathname) &&
    hasExactAttributionQuery(candidate.url, candidate.provider)
  );
}

/**
 * Two booking links name the same calendar when they are identical or one is
 * the other's attributed form. The SDK re-renders with the attributed URL once
 * a booking intent resolves; a surface that read that as a new calendar would
 * reset the one the visitor had just opened.
 */
export function isSameBookingRoute(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return isMatchingAttributedBookingUrl(a, b) || isMatchingAttributedBookingUrl(b, a);
}

function safeHttpsUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    const url = new URL(value);
    if (
      url.protocol !== "https:" ||
      !url.hostname ||
      url.username ||
      url.password
    ) {
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

/**
 * Await a bounded SDK resolver, accepting only the original HTTPS destination
 * or the same provider route with exactly one valid Floe reference. A broken or
 * hostile callback can therefore delay a booking by at most its own bound; it
 * can never redirect the prospect elsewhere.
 */
export async function resolveBookingIntentDestination(
  originalUrl: string,
  resolver?: BookingIntentResolver,
): Promise<string> {
  const safeOriginal = safeHttpsUrl(originalUrl);
  if (!safeOriginal) return "";
  if (!resolver) return safeOriginal;

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    const candidate = await Promise.race([
      Promise.resolve().then(() => resolver(safeOriginal)),
      new Promise<null>((resolve) => {
        timeoutId = setTimeout(
          () => resolve(null),
          BOOKING_ATTRIBUTION_TIMEOUT_MS,
        );
      }),
    ]);
    if (candidate === safeOriginal) return safeOriginal;
    return isMatchingAttributedBookingUrl(safeOriginal, candidate)
      ? candidate
      : safeOriginal;
  } catch {
    return safeOriginal;
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

/**
 * Preserve the browser gesture for a first-click `_blank` booking CTA while the
 * bounded attribution request runs. The blank child is opened synchronously,
 * detached from its opener immediately, and navigated only after the resolved
 * destination passes {@link resolveBookingIntentDestination}. Returning false
 * lets the real anchor perform its ordinary fallback when a popup blocker
 * refuses even the gesture-time blank window.
 */
export function openResolvedBookingWindow(
  originalUrl: string,
  resolver?: BookingIntentResolver,
): boolean {
  const safeOriginal = safeHttpsUrl(originalUrl);
  if (!safeOriginal || typeof window === "undefined") return false;

  const child = window.open("about:blank", "_blank");
  if (!child) {
    // Preserve click telemetry / a possible server-side correlation for a later
    // attempt, while the native anchor handles this blocked-window fallback.
    void resolveBookingIntentDestination(safeOriginal, resolver);
    return false;
  }

  try {
    child.opener = null;
    const referrerPolicy = child.document.createElement("meta");
    referrerPolicy.name = "referrer";
    referrerPolicy.content = "no-referrer";
    child.document.head.appendChild(referrerPolicy);
    child.document.title = "Opening scheduling…";
  } catch {
    // The initial about:blank should be same-origin. If a browser implements it
    // differently, opener isolation above and destination validation still hold.
  }

  void resolveBookingIntentDestination(safeOriginal, resolver).then(
    (destination) => {
      if (!destination || child.closed) return;
      try {
        child.location.replace(destination);
      } catch {
        try {
          child.location.href = destination;
        } catch {
          // The prospect closed or independently navigated the placeholder.
        }
      }
    },
  );
  return true;
}

export async function fetchAttributedBookingUrl(
  options: BookingAttributionLinkOptions,
): Promise<string | null> {
  if (
    !options.clientKey.trim() ||
    !options.visitorToken.trim() ||
    !supportedBookingRoute(options.originalUrl)
  ) {
    return null;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    BOOKING_ATTRIBUTION_TIMEOUT_MS,
  );
  try {
    const response = await (options.fetcher ?? fetch)(
      `${options.apiUrl.replace(/\/+$/, "")}/sites/web-agent/booking-link`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientKey: options.clientKey,
          visitorToken: options.visitorToken,
          ...(options.conversationId
            ? { conversationId: options.conversationId }
            : {}),
        }),
        credentials: "omit",
        referrerPolicy: "no-referrer",
        signal: controller.signal,
      },
    );
    if (!response.ok) return null;
    const result = (await response.json()) as {
      success?: unknown;
      data?: { destinationUrl?: unknown };
    };
    const candidate =
      result.success === true ? result.data?.destinationUrl : null;
    return isMatchingAttributedBookingUrl(options.originalUrl, candidate)
      ? candidate
      : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}
